import {
  PropsWithChildren,
  useEffect,
  useMemo,
  createContext,
  useContext,
  useRef,
  useCallback,
} from 'react';

import { GlobalStyles } from '@mui/material';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router-dom';

import {
  NormalizedTourStep,
  TourApi,
  TourState,
  TourConfig,
} from 'core/tour-v2/types';
import { useNavigate } from '@cast/infrastructure-organization';

import { normalizeSteps, getTourStepUrl } from './_utils/tour';
import { tourStepKey, tourKeyParam } from './constants';
import { TourTooltip } from './tooltip/TourTooltip';

type Props = PropsWithChildren;

const TourStateContext = createContext<TourState>({} as never);

const useWatchNext = (
  tourApi: TourApi,
  currentStep?: NormalizedTourStep,
  nextStep?: NormalizedTourStep
) => {
  useEffect(() => {
    if (!currentStep?.goNext) {
      return;
    }

    switch (currentStep.goNext.type) {
      case 'after-timeout': {
        const timeoutId = setTimeout(() => {
          tourApi.next();
        }, currentStep.goNext.timeoutMs);

        return () => {
          clearTimeout(timeoutId);
        };
      }
      case 'on-click': {
        const matcher = currentStep.goNext.matcher;
        const allowEventPropagation = currentStep.goNext.allowEventPropagation;

        const handleClick = (event: Event) => {
          if (matcher(event)) {
            if (!allowEventPropagation) {
              event.stopPropagation();
              event.preventDefault();
            }

            tourApi.next();
          }
        };

        // do not use capture in pass-through handlers since state updates might cause re-renders and cancel event processing.
        const capture = !allowEventPropagation;

        document.addEventListener('click', handleClick, { capture });

        return () => {
          document.removeEventListener('click', handleClick, { capture });
        };
      }
    }
  }, [currentStep, nextStep, tourApi]);
};

const useTourData = (api: TourApi, currentStep?: NormalizedTourStep) => {
  const dataRef = useRef<Record<string, unknown>>({});
  if (currentStep && !dataRef.current[currentStep.id]) {
    dataRef.current[currentStep.id] = currentStep.state(api);
  }
  return dataRef.current;
};

const store = getDefaultStore();
const configsAtom = atom<Map<string, TourConfig>>(new Map());

export const TourProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const configsMap = useAtomValue(configsAtom, { store });

  // Read active tour state from URL
  const activeTourKey = searchParams.get(tourKeyParam);
  const currentStepId = searchParams.get(tourStepKey);

  // Get active config based on URL tourKey
  const activeConfig = activeTourKey ? configsMap.get(activeTourKey) : null;

  // Normalize steps for the active config
  const normalizedSteps = useMemo(
    () =>
      activeConfig && activeConfig.steps.length > 0
        ? normalizeSteps(activeConfig.steps, activeConfig.tourKey)
        : [],
    [activeConfig]
  );

  const currentStepIndex = normalizedSteps.findIndex(
    (step) => step.id === currentStepId
  );
  const prevStep = normalizedSteps[currentStepIndex - 1];
  const currentStep = normalizedSteps[currentStepIndex];
  const nextStep = normalizedSteps[currentStepIndex + 1];
  const totalSteps = normalizedSteps.length;

  const navigateToStep = useCallback(
    (step: NormalizedTourStep | undefined, tourKey: string) => {
      if (!step) {
        return;
      }

      if (step.url) {
        navigate(
          getTourStepUrl({
            baseUrl: step.url,
            tourKey,
            tourStepId: step.id,
          })
        );
      } else {
        searchParams.set(tourKeyParam, tourKey);
        searchParams.set(tourStepKey, step.id);
        setSearchParams(searchParams);
      }
    },
    [navigate, searchParams, setSearchParams]
  );

  const clearTourParams = useCallback(() => {
    searchParams.delete(tourKeyParam);
    searchParams.delete(tourStepKey);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const tourApiRef = useRef<TourApi>({} as never);

  tourApiRef.current.setConfig = useCallback((config: TourConfig) => {
    const newConfigs = new Map(store.get(configsAtom));
    newConfigs.set(config.tourKey, config);
    store.set(configsAtom, newConfigs);
  }, []);

  tourApiRef.current.start = useCallback(
    (tourKey: string) => {
      const configToStart = store.get(configsAtom).get(tourKey);
      if (!configToStart) {
        return;
      }

      const steps = normalizeSteps(configToStart.steps, tourKey);
      const firstStep = steps[0];
      if (!firstStep) {
        return;
      }

      // Stop any currently active tour first
      if (activeTourKey && activeTourKey !== tourKey) {
        clearTourParams();
      }

      navigateToStep(firstStep, tourKey);
    },
    [activeTourKey, clearTourParams, navigateToStep]
  );

  tourApiRef.current.finish = useCallback(() => {
    clearTourParams();
    activeConfig?.onComplete?.();
  }, [activeConfig, clearTourParams]);

  tourApiRef.current.next = useCallback(() => {
    if (!activeTourKey) {
      return;
    }

    // If there's no next step, finish the tour
    if (!nextStep) {
      clearTourParams();
      activeConfig?.onComplete?.();
      return;
    }

    navigateToStep(nextStep, activeTourKey);
  }, [activeTourKey, activeConfig, nextStep, navigateToStep, clearTourParams]);

  tourApiRef.current.prev = useCallback(() => {
    if (!activeTourKey) {
      return;
    }
    navigateToStep(prevStep, activeTourKey);
  }, [activeTourKey, prevStep, navigateToStep]);

  const tourApi = tourApiRef.current;

  useWatchNext(tourApi, currentStep, nextStep);

  useEffect(() => {
    currentStep?.onBefore?.();
    return () => {
      currentStep?.onAfter?.();
    };
  }, [currentStep]);

  const tourData = useTourData(tourApi, currentStep);

  return (
    <TourStateContext.Provider
      value={{
        api: tourApi,
        activeTourKey,
        tourData,
        allSteps: normalizedSteps,
        currentStepData: tourData?.[currentStep?.id],
        currentStep,
        currentStepId,
        isTourActive: !!currentStep,
        totalSteps,
        currentStepIndex,
      }}
    >
      {!!currentStep?.globalStyles && (
        <GlobalStyles styles={currentStep.globalStyles} />
      )}
      {children}
      {currentStep?.overlay}
      {!!currentStep?.tooltip && (
        <TourTooltip key={currentStep.id} config={currentStep.tooltip} />
      )}
    </TourStateContext.Provider>
  );
};

export const useTourV2 = <
  PAGE_ID_TO_PAGE_STATE extends Record<string, unknown> = Record<
    string,
    unknown
  >
>(): TourState<PAGE_ID_TO_PAGE_STATE> => {
  return useContext(TourStateContext) as TourState<PAGE_ID_TO_PAGE_STATE>;
};

export const createUsePageTourData = <
  PAGE_ID_TO_PAGE_STATE extends Record<string, unknown>
>() => {
  return <PAGE_ID extends keyof PAGE_ID_TO_PAGE_STATE>(
    page: PAGE_ID
  ): PAGE_ID_TO_PAGE_STATE[PAGE_ID] | undefined => {
    const { currentStep, currentStepData } = useTourV2<PAGE_ID_TO_PAGE_STATE>();

    if (currentStep?.page !== page) {
      return undefined;
    }
    return currentStepData as PAGE_ID_TO_PAGE_STATE[PAGE_ID];
  };
};

export const createTour = <
  PAGE_ID_TO_PAGE_STATE extends Record<string, unknown> = Record<
    string,
    unknown
  >
>(
  tourKey: string
) => {
  const usePageTourData = <PAGE_ID extends keyof PAGE_ID_TO_PAGE_STATE>(
    page: PAGE_ID
  ): PAGE_ID_TO_PAGE_STATE[PAGE_ID] | undefined => {
    const { currentStep, currentStepData, activeTourKey } =
      useTourV2<PAGE_ID_TO_PAGE_STATE>();

    if (currentStep?.page !== page || activeTourKey !== tourKey) {
      return undefined;
    }
    return currentStepData as PAGE_ID_TO_PAGE_STATE[PAGE_ID];
  };

  const useTour = (): TourState<PAGE_ID_TO_PAGE_STATE> => {
    const state = useTourV2<PAGE_ID_TO_PAGE_STATE>();
    const isThisTour = state.activeTourKey === tourKey;

    return {
      ...state,
      isTourActive: isThisTour && state.isTourActive,
      currentStep: isThisTour ? state.currentStep : undefined,
      currentStepData: isThisTour ? state.currentStepData : undefined,
      currentStepId: isThisTour ? state.currentStepId : null,
      currentStepIndex: isThisTour ? state.currentStepIndex : -1,
      totalSteps: isThisTour ? state.totalSteps : 0,
      allSteps: isThisTour ? state.allSteps : [],
      api: {
        ...state.api,
        start: () => state.api.start(tourKey),
      },
    };
  };

  const useIsTourActive = () => {
    const { activeTourKey: active, currentStepId } = useTourV2();
    return active === tourKey && currentStepId != null;
  };

  return { usePageTourData, useTour, useIsTourActive };
};
